import {
    useTransition,
    MouseEvent,
    TouchEvent,
    useRef,
    useEffect,
    useState
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { PreloadElement, usePreload } from "./PreloadProvider";

interface IProps {
    to?: string;
    onTransition?: () => void;
    onTransitionEnd?: () => void;
    preloadElement?: PreloadElement;
}

const HOVER_TIMER = 100;
const TRANSITION_TIMER = 50;

export const useNavigationTransition = ({
    to,
    onTransition,
    onTransitionEnd,
    preloadElement
}: IProps) => {
    const navigate = useNavigate();
    const { pathname } = useLocation();

    const { setPreloadRoute, setPreloadElement } = usePreload();
    const [isPending, startTransition] = useTransition();
    const hoverTimerRef = useRef<number | undefined>();

    const onClick = (e: MouseEvent<HTMLElement> | undefined) => {
        e?.preventDefault();
        startTransition(() => {
            if (onTransition !== undefined) {
                onTransition();
            }
            if (to !== undefined && to !== pathname) {
                void navigate(to);
            }
        });
    };

    const onMouseDown = (_: MouseEvent<HTMLElement>) => {
        if (to !== undefined) {
            setPreloadRoute(to);
        }
        if (preloadElement !== undefined) {
            setPreloadElement(preloadElement);
        }
    };

    const onTouchStart = (_: TouchEvent<HTMLElement>) => {
        if (to !== undefined) {
            setPreloadRoute(to);
        }
        if (preloadElement !== undefined) {
            setPreloadElement(preloadElement);
        }
    };

    const onMouseEnter = (_: MouseEvent<HTMLElement>) => {
        if (to !== undefined) {
            hoverTimerRef.current = window.setTimeout(() => {
                setPreloadRoute(to);
            }, HOVER_TIMER);
        }
        if (preloadElement !== undefined) {
            hoverTimerRef.current = window.setTimeout(() => {
                setPreloadElement(preloadElement);
            }, HOVER_TIMER);
        }
    };

    const onMouseLeave = (_: MouseEvent<HTMLElement>) => {
        if (hoverTimerRef.current !== undefined) {
            window.clearTimeout(hoverTimerRef.current);
            hoverTimerRef.current = undefined;
        }
    };

    const currentPending = useRef(false);
    useEffect(() => {
        if (!isPending && currentPending.current) {
            if (onTransitionEnd) {
                onTransitionEnd();
            }
        }
        currentPending.current = isPending;
    }, [isPending, onTransitionEnd]);

    const [delayedPending, setDelayedPending] = useState(false);
    useEffect(() => {
        if (isPending) {
            const timeout = setTimeout(() => {
                setDelayedPending(true);
            }, TRANSITION_TIMER);
            return () => {
                clearTimeout(timeout);
            };
        } else {
            setDelayedPending(false);
        }
    }, [isPending]);

    return {
        spread: {
            onClick,
            onMouseDown,
            onTouchStart,
            onMouseEnter,
            onMouseLeave
        },
        isPending: delayedPending,
        startTransition
    };
};
